/**
 * host-graph — the pure geometry behind the Host view's load/CPU time-series
 * line graph (W4 frontend).
 *
 * Projects a series of (possibly null) gauge readings, oldest → newest, into SVG
 * polyline point strings inside a fixed viewBox. A `null` reading (a failed probe
 * that sample) is a GAP, not a zero: the series breaks into multiple contiguous
 * segments around it so the line never dives to the floor and lies about the
 * host being idle. Kept free of React/DOM so the projection is unit-tested
 * directly; the {@link HostLoadGraph} component is the thin SVG shell over it.
 */

/** The drawing box + value domain a series is projected into. */
export interface GraphScale {
  /** viewBox width in user units. */
  width: number
  /** viewBox height in user units. */
  height: number
  /** Vertical clearance (px) so a peak/trough never clips the edge. */
  pad: number
  /** Domain max; readings are clamped to `[0, max]` before projection. */
  max: number
}

/**
 * Project `values` (oldest → newest, nulls = gaps) into SVG polyline `points`
 * strings — one per contiguous run of non-null readings. A single point with no
 * neighbours still yields a one-coordinate segment (the component renders it as a
 * dot) so a lone reading is visible rather than dropped. An all-null / empty
 * series yields `[]` (the component shows a flat baseline).
 */
export function seriesSegments(values: readonly (number | null)[], scale: GraphScale): string[] {
  const { width, height, pad, max } = scale
  const n = values.length
  const safeMax = max > 0 ? max : 1
  const xFor = (i: number): number => (n <= 1 ? width / 2 : (i / (n - 1)) * width)
  const yFor = (v: number): number => {
    const clamped = Math.min(safeMax, Math.max(0, v))
    // Invert Y so a high reading sits near the top of the SVG.
    return height - pad - (clamped / safeMax) * (height - pad * 2)
  }

  const segments: string[] = []
  let current: string[] = []
  const flush = (): void => {
    if (current.length > 0) {
      segments.push(current.join(' '))
      current = []
    }
  }
  values.forEach((v, i) => {
    if (v === null) {
      flush()
      return
    }
    current.push(`${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`)
  })
  flush()
  return segments
}

/**
 * A "nice" domain ceiling for an auto-scaled series (e.g. load average, which is
 * unbounded unlike CPU%). Rounds the peak reading up to a whole number, never
 * below `floor`, so a flat near-zero series still has a sane axis and a busy one
 * gets headroom above the peak. Null readings are ignored.
 */
export function niceMax(values: readonly (number | null)[], floor = 1): number {
  let peak = 0
  for (const v of values) {
    if (v !== null && v > peak) {
      peak = v
    }
  }
  return Math.max(floor, Math.ceil(peak))
}
